import { Badge, Col, Row } from "reactstrap";
import { ComponentSpinner, LoaderOverlay } from "@components/spinner";
import { getPlural, getStatusBadge } from "@src/utility/helpers/utils";

import { DATE_FORMAT_VIEW } from "@src/utility/constants";
import Flex from "@components/ui-components/flex";
import LoadingButton from "@components/ui-components/loading-button";
import { NoDataComponent } from "@components/no-data-component";
import classNames from "classnames";
import size from "lodash/size";
import useDateHandler from "@src/utility/hooks/use-date-handler";
import useTickets from "./use-tickets";
import { useTranslation } from "react-i18next";

const Tickets = props => {
    const {
        loading,
        tickets,
        getTicketDetails,
        detailsLoading,
        getTickets,
        ticketDetails
    } = useTickets(props);

    const { dateFormatter } = useDateHandler();
    const { t } = useTranslation();

    return (
        <>
            <Flex justify="between" className="mb-1 border-bottom pb-1">
                <h4>
                    {t(getPlural({ text: "Ticket", length: tickets.count }))} (
                    {tickets.count})
                </h4>
            </Flex>
            <LoaderOverlay
                active={loading}
                children={
                    size(tickets.data) ? (
                        <Row className="match-height">
                            <Col
                                lg={4}
                                className="border-start border-end overflow-scroll mb-25"
                                style={{ height: "500px" }}>
                                {tickets.data.map(ticket => {
                                    return (
                                        <div
                                            key={ticket.id}
                                            onClick={() => getTicketDetails(
                                                ticket.id
                                            )
                                            }
                                            className={classNames(
                                                "ticket border-bottom p-1 cursor-pointer",
                                                {
                                                    "bg-light-primary":
                                                                ticket.id ===
                                                                tickets.active
                                                }
                                            )}>
                                            <h6>{ticket.title}</h6>
                                            <Flex
                                                justify="end"
                                                align="center">
                                                <Badge
                                                    color={
                                                        getStatusBadge(
                                                            ticket.ticket_state
                                                        ).color
                                                    }>
                                                    {
                                                        getStatusBadge(
                                                            ticket.ticket_state
                                                        ).title
                                                    }
                                                </Badge>
                                            </Flex>
                                        </div>
                                    );
                                })}
                                <div className="px-2 mt-1 position-sticky bottom-0 bg-white z-1">
                                    <Flex
                                        justify="center"
                                        align="center">
                                        {tickets.nextBatch !== null ? (
                                            <LoadingButton
                                                text="View More"
                                                onClick={() => getTickets()
                                                }/>
                                        ) : null}
                                    </Flex>
                                </div>
                            </Col>
                            <Col lg={8} className="mb-25 border">
                                {detailsLoading ? (
                                    <ComponentSpinner />
                                ) : (
                                    ticketDetails && (
                                        <>
                                            <h3 className="active-ticket-title mt-50">
                                                {
                                                    ticketDetails?.[
                                                        "title"
                                                    ]
                                                }
                                            </h3>
                                            <Row className="mt-2">
                                                <Col
                                                    lg={4}
                                                    className="border-end">
                                                    <div>
                                                        <h6 className="fw-bolder text-black">
                                                            {t(
                                                                "Ticket Owner"
                                                            )}
                                                        </h6>
                                                        <span>
                                                            {
                                                                ticketDetails?.[
                                                                    "assigned_admin"
                                                                ]?.[
                                                                    "name"
                                                                ]
                                                            }
                                                        </span>
                                                        <span>
                                                            {
                                                                ticketDetails?.[
                                                                    "assigned_admin"
                                                                ]?.[
                                                                    "email"
                                                                ]
                                                            }
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col
                                                    lg={4}
                                                    className="border-end">
                                                    <div>
                                                        <h6 className="fw-bolder text-black">
                                                            {t(
                                                                "Created On"
                                                            )}
                                                        </h6>
                                                        <span>
                                                            {ticketDetails?.[
                                                                "created_at"
                                                            ] ? dateFormatter(
                                                                    {
                                                                        date: ticketDetails?.[
                                                                            "created_at"
                                                                        ],
                                                                        formatAs:
                                                                                          DATE_FORMAT_VIEW
                                                                    }
                                                                ) : null}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1">
                                                        <h6 className="fw-bolder text-black">
                                                            {t(
                                                                "Updated On"
                                                            )}
                                                        </h6>
                                                        <span>
                                                            {ticketDetails?.[
                                                                "updated_at"
                                                            ] ? dateFormatter(
                                                                    {
                                                                        date: ticketDetails?.[
                                                                            "updated_at"
                                                                        ],
                                                                        formatAs:
                                                                                          DATE_FORMAT_VIEW
                                                                    }
                                                                ) : null}
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col lg={4}>
                                                    <div>
                                                        <h6 className="fw-bolder text-black">
                                                            {t(
                                                                "Status"
                                                            )}
                                                        </h6>
                                                        <Badge
                                                            color={
                                                                getStatusBadge(
                                                                    ticketDetails?.[
                                                                        "ticket_state"
                                                                    ]
                                                                )
                                                                    .color
                                                            }>
                                                            {
                                                                getStatusBadge(
                                                                    ticketDetails?.[
                                                                        "ticket_state"
                                                                    ]
                                                                )
                                                                    .title
                                                            }
                                                        </Badge>
                                                    </div>
                                                    <div className="mt-1">
                                                        <h6 className="fw-bolder text-black">
                                                            {t(
                                                                "Type"
                                                            )}
                                                        </h6>
                                                        <Badge>
                                                            {
                                                                ticketDetails?.[
                                                                    "ticket_type"
                                                                ]
                                                            }
                                                        </Badge>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <hr />
                                            <div>
                                                <h3>
                                                    {t(
                                                        "Description"
                                                    )}
                                                </h3>
                                                {
                                                    ticketDetails?.[
                                                        "description"
                                                    ]
                                                }
                                            </div>
                                        </>
                                    )
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <NoDataComponent />
                    )
                }/>
        </>
    );
};

export default Tickets;
